import { useMutation } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { returnCasesHttp } from '@/services/returnCases'
import type { FactType, FactVersion, ReturnCaseWorkspace } from '@/types/returnCase.types'

const FACT_OPTIONS: Array<{ key: string; type: FactType; example: string }> = [
  { key: 'synthetic.gross_amount', type: 'MONEY', example: '{"amount":"15000.00","currency":"INR","scale":2}' },
  { key: 'synthetic.item_count', type: 'INTEGER', example: '1' },
  { key: 'synthetic.note', type: 'STRING', example: '"Supporting note"' },
  { key: 'synthetic.details', type: 'STRUCTURED', example: '{"schema_version":"1.0","data":{}}' },
]

interface Props {
  caseId: string
  workspace: ReturnCaseWorkspace
  canEdit: boolean
  onChanged: () => Promise<void>
}

export default function FactWorkspacePanel({ caseId, workspace, canEdit, onChanged }: Props) {
  const modules = workspace.modules ?? []
  const facts = workspace.facts ?? []
  const derived = workspace.derived_values ?? []
  const [factKey, setFactKey] = useState(FACT_OPTIONS[0].key)
  const selected = useMemo(() => FACT_OPTIONS.find(item => item.key === factKey) ?? FACT_OPTIONS[0], [factKey])
  const [valueText, setValueText] = useState(FACT_OPTIONS[0].example)
  const [sourceReference, setSourceReference] = useState('web:manual-entry')
  const [reason, setReason] = useState('Initial manual entry')
  const [history, setHistory] = useState<FactVersion[]>([])
  const [error, setError] = useState('')

  const createModule = useMutation({
    mutationFn: () => returnCasesHttp.createModule(caseId, {
      expected_revision: workspace.current_revision,
      module_type: 'SYNTHETIC', instance_key: 'primary', label: 'Synthetic fact fixture', schema_version: '1.0',
    }),
    onSuccess: async () => { setError(''); await onChanged() },
    onError: async () => { setError('Module was not created. Reloaded server state must be reviewed before retrying.'); await onChanged() },
  })
  const saveFact = useMutation({
    mutationFn: async () => {
      if (!modules[0]) throw new Error('Create a module first')
      let value: unknown
      try { value = JSON.parse(valueText) } catch { throw new Error('Value must be valid JSON') }
      return returnCasesHttp.saveFact(caseId, factKey, {
        expected_revision: workspace.current_revision,
        module_instance_id: modules[0].id,
        fact_type: selected.type,
        schema_version: '1.0', value,
        source_type: 'STAFF_MANUAL', source_reference: sourceReference, reason,
      })
    },
    onSuccess: async () => { setError(''); await onChanged() },
    onError: async errorValue => {
      const message = errorValue instanceof Error ? errorValue.message : 'The case changed.'
      setError(`Fact was not saved: ${message} Values are never auto-merged; review the refreshed revision before retrying.`)
      await onChanged()
    },
  })
  const loadHistory = useMutation({
    mutationFn: (factId: string) => returnCasesHttp.factHistory(caseId, factId),
    onSuccess: setHistory,
    onError: () => setError('Fact history could not be loaded.'),
  })

  const chooseFact = (key: string) => {
    const option = FACT_OPTIONS.find(item => item.key === key) ?? FACT_OPTIONS[0]
    setFactKey(option.key)
    setValueText(option.example)
  }

  return (
    <section className="mt-8 border-t border-border pt-6" aria-labelledby="fact-workspace-title">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="fact-workspace-title" className="text-lg font-semibold">Revisioned tax facts</h2>
          <p className="text-sm text-text-secondary">Typed values retain immutable provenance and explicit history.</p>
        </div>
        <span className="badge">Projection {workspace.projection_hash?.slice(0, 12) ?? 'not built'}</span>
      </div>
      {error && <div className="alert alert-error mt-4" role="alert">{error}</div>}

      {modules.length === 0 ? (
        <div className="mt-4 rounded border border-border p-4">
          <p>No fact module exists for this case.</p>
          {canEdit && <button className="btn btn-secondary mt-3" disabled={createModule.isPending} onClick={() => createModule.mutate()}>Create synthetic module</button>}
        </div>
      ) : (
        <div className="mt-4 grid gap-4 xl:grid-cols-2">
          <div className="rounded border border-border p-4">
            <h3 className="font-medium">Accepted facts</h3>
            {facts.length === 0 && <p className="mt-2 text-sm text-text-secondary">No facts have been accepted.</p>}
            <ul className="mt-3 space-y-3">
              {facts.map(fact => (
                <li key={fact.id} className="rounded bg-surface-secondary p-3">
                  <div className="flex flex-wrap justify-between gap-2"><span className="font-mono text-sm">{fact.fact_key}</span><span className="badge">v{fact.current_version_number} · {fact.status}</span></div>
                  <pre className="mt-2 overflow-auto text-xs">{JSON.stringify(fact.current_version?.value)}</pre>
                  <div className="mt-2 text-xs text-text-secondary">{fact.current_version?.source_type} · {fact.current_version?.source_reference}</div>
                  <button className="link mt-2 text-sm" onClick={() => loadHistory.mutate(fact.id)}>View immutable history</button>
                </li>
              ))}
            </ul>
          </div>
          {canEdit && (
            <form className="rounded border border-border p-4" onSubmit={event => { event.preventDefault(); saveFact.mutate() }}>
              <h3 className="font-medium">Enter a typed fact</h3>
              <label className="mt-3 block text-sm" htmlFor="fact-key">Registered fact</label>
              <select id="fact-key" className="input mt-1 w-full" value={factKey} onChange={event => chooseFact(event.target.value)}>
                {FACT_OPTIONS.map(option => <option key={option.key} value={option.key}>{option.key} ({option.type})</option>)}
              </select>
              <label className="mt-3 block text-sm" htmlFor="fact-value">JSON value</label>
              <textarea id="fact-value" className="input mt-1 min-h-24 w-full font-mono text-xs" value={valueText} onChange={event => setValueText(event.target.value)} />
              <label className="mt-3 block text-sm" htmlFor="fact-source">Source reference</label>
              <input id="fact-source" className="input mt-1 w-full" value={sourceReference} onChange={event => setSourceReference(event.target.value)} />
              <label className="mt-3 block text-sm" htmlFor="fact-reason">Reason</label>
              <input id="fact-reason" className="input mt-1 w-full" value={reason} onChange={event => setReason(event.target.value)} />
              <button className="btn btn-primary mt-4" disabled={saveFact.isPending} type="submit">Save new fact version</button>
            </form>
          )}
        </div>
      )}

      {derived.length > 0 && <div className="mt-4 rounded border border-border p-4"><h3 className="font-medium">Derived values <span className="badge">Read only</span></h3>{derived.map(item => <div key={item.id} className="mt-2 text-sm"><span className="font-mono">{item.fact_key}</span> · derivation {item.derivation_version}</div>)}</div>}
      {history.length > 0 && <div className="mt-4 rounded border border-border p-4"><h3 className="font-medium">Fact history</h3><ol className="mt-2 space-y-2">{history.map(item => <li key={item.id} className="text-sm">v{item.version_number} · revision {item.case_revision} · {item.source_type} · {item.reason}</li>)}</ol></div>}
    </section>
  )
}
